import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, db, transactWithRetry, batchTransact, SYSTEM_TEMPLATE_ORG_ID } from '@/lib/db'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { Loader2, Building2, LogOut } from 'lucide-react'
import type { Organization, Profile } from '@/types'
import { id } from '@instantdb/react'

export function JoinPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [inviteCode, setInviteCode] = useState('')
  const [isJoining, setIsJoining] = useState(false)

  // 查询当前用户档案
  const { data: profileData } = db.useQuery({
    profiles: {
      $: {
        where: { id: auth.user?.id },
      },
    },
  })

  const currentProfile = profileData?.profiles?.[0] as Profile | undefined

  // 如果已有企业，跳转到首页
  if (currentProfile?.org_id) {
    navigate('/', { replace: true })
    return null
  }

  const cloneTemplateProjects = async (targetOrgId: string) => {
    try {
      // 1. 查询目标企业现有项目
      const { data: orgData } = await db.queryOnce({
        projects: {
          $: {
            where: { org_id: targetOrgId },
          },
        },
      })

      if ((orgData?.projects || []).length > 0) return

      // 2. 查询模板企业下的所有内容
      const { data: templateData } = await db.queryOnce({
        projects: { $: { where: { org_id: SYSTEM_TEMPLATE_ORG_ID } } },
        note_styles: { $: { where: { org_id: SYSTEM_TEMPLATE_ORG_ID } } },
        note_templates: { $: { where: { org_id: SYSTEM_TEMPLATE_ORG_ID } } },
        note_image_slots: { $: { where: { org_id: SYSTEM_TEMPLATE_ORG_ID } } },
        copywriting_styles: { $: { where: { org_id: SYSTEM_TEMPLATE_ORG_ID } } },
      })

      const templates = templateData as any
      const txns: any[] = []

      // ID 映射表，用于保持关联关系
      const styleIdMap: Record<string, string> = {}
      const templateIdMap: Record<string, string> = {}

      // A. 克隆笔记风格
      ;(templates.note_styles || []).forEach((style: any) => {
        const newId = id()
        styleIdMap[style.id] = newId
        txns.push(db.tx.note_styles[newId].update({
          ...style,
          id: newId,
          org_id: targetOrgId,
          created_at: Date.now(),
        }))
      })

      // B. 克隆文案风格
      ;(templates.copywriting_styles || []).forEach((cpy: any) => {
        const newId = id()
        const newStyleId = styleIdMap[cpy.style_id]
        if (newStyleId) {
          txns.push(db.tx.copywriting_styles[newId].update({
            ...cpy,
            id: newId,
            style_id: newStyleId,
            org_id: targetOrgId,
            created_at: Date.now(),
          }))
        }
      })

      // C. 克隆配图模板
      ;(templates.note_templates || []).forEach((tpl: any) => {
        const newId = id()
        const newStyleId = styleIdMap[tpl.style_id]
        if (newStyleId) {
          templateIdMap[tpl.id] = newId
          txns.push(db.tx.note_templates[newId].update({
            ...tpl,
            id: newId,
            style_id: newStyleId,
            org_id: targetOrgId,
            created_at: Date.now(),
          }))
        }
      })

      // D. 克隆配图槽位
      ;(templates.note_image_slots || []).forEach((slot: any) => {
        const newId = id()
        const newTemplateId = templateIdMap[slot.template_id]
        if (newTemplateId) {
          txns.push(db.tx.note_image_slots[newId].update({
            ...slot,
            id: newId,
            template_id: newTemplateId,
            org_id: targetOrgId,
            created_at: Date.now(),
          }))
        }
      })

      // E. 克隆项目
      ;(templates.projects || []).forEach((project: any) => {
        const newId = id()
        const newStyleId = styleIdMap[project.note_style_id]
        txns.push(db.tx.projects[newId].update({
          ...project,
          id: newId,
          org_id: targetOrgId,
          note_style_id: newStyleId || project.note_style_id,
          created_at: Date.now(),
        }))
      })

      if (txns.length > 0) {
        await batchTransact(txns)
        console.log(`[模板初始化] 已为企业 ${targetOrgId} 克隆 ${txns.length} 条记录`)
      }
    } catch (error) {
      console.error('[模板初始化] 克隆失败:', error)
    }
  }

  const handleJoinOrganization = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!inviteCode || inviteCode.trim().length === 0) {
      toast.error('请输入邀请码')
      return
    }

    if (!auth.user) {
      toast.error('请先登录')
      return
    }

    setIsJoining(true)
    try {
      // 1. 查询邀请码对应的企业
      const { data } = await db.queryOnce({
        organizations: {
          $: {
            where: { invite_code: inviteCode.trim().toUpperCase() },
          },
        },
      })

      const organization = data?.organizations?.[0] as Organization | undefined

      if (!organization) {
        toast.error('邀请码无效，请联系企业管理员')
        return
      }

      // 2. 创建或更新用户档案
      const profileId = auth.user.id
      await transactWithRetry(
        db.tx.profiles[profileId].update({
          id: profileId,
          email: auth.user.email || '',
          org_id: organization.id,
          created_at: Date.now(),
        })
      )

      // 3. 初始化模板项目（如果企业尚未初始化）
      if (!organization.is_initialized) {
        // 先标记为已初始化，防止并发重复
        await transactWithRetry(
          db.tx.organizations[organization.id].update({ is_initialized: true })
        )
        await cloneTemplateProjects(organization.id)
      }

      toast.success(`成功加入企业：${organization.name}`)
      
      // 4. 跳转到首页
      setTimeout(() => {
        navigate('/', { replace: true })
      }, 500)
    } catch (error) {
      console.error('加入企业失败:', error)
      toast.error('加入失败，请稍后重试')
    } finally {
      setIsJoining(false)
    }
  }

  const handleLogout = async () => {
    try {
      await db.auth.signOut()
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('退出登录失败:', error)
      toast.error('退出登录失败，请重试')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">加入企业</CardTitle>
          <CardDescription>
            输入企业邀请码以开始使用
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
            <p className="font-medium mb-1">👋 欢迎，{auth.user?.email}</p>
            <p className="text-xs text-blue-700">
              您需要输入企业邀请码才能继续使用系统
            </p>
          </div>

          <form onSubmit={handleJoinOrganization} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="inviteCode" className="text-sm font-medium">
                企业邀请码
              </label>
              <Input
                id="inviteCode"
                type="text"
                placeholder="例如：ABC-888"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                disabled={isJoining}
                className="bg-white uppercase tracking-wider text-center text-lg font-mono"
                autoFocus
                maxLength={20}
              />
              <p className="text-xs text-muted-foreground">
                请联系企业管理员获取邀请码
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isJoining || !inviteCode}
            >
              {isJoining ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  验证中...
                </>
              ) : (
                <>
                  <Building2 className="w-4 h-4 mr-2" />
                  加入企业
                </>
              )}
            </Button>
          </form>

          <div className="pt-4 border-t">
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-muted-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              退出登录
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

